/**
 * AuthApi — thin axios layer around the new /api/auth/member/* endpoints.
 * All other auth state lives in useMemberAuth.
 */
import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "archive_member_token";

const client = axios.create({
  baseURL: `${BASE}/api/auth/member`,
  timeout: 20000,
});

export const saveToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);
export const getToken = () => localStorage.getItem(TOKEN_KEY);

const _authHeaders = () => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export const formatApiError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export const authApi = {
  signup: async ({ email, password, password_confirm, callsign }) =>
    client.post("/signup", { email, password, password_confirm, callsign: callsign || null }),

  verifyEmail: async (token) =>
    client.post("/verify-email", { token }),

  resendVerification: async (email) =>
    client.post("/resend-verification", { email }),

  login: async ({ email, password, remember_me }) =>
    client.post("/login", { email, password, remember_me: !!remember_me }),

  logout: async () =>
    client.post("/logout", null, { headers: _authHeaders() }),

  me: async () =>
    client.get("/me", { headers: _authHeaders() }),

  forgotPassword: async (email) =>
    client.post("/forgot-password", { email }),

  resetPassword: async ({ token, password, password_confirm }) =>
    client.post("/reset-password", { token, password, password_confirm }),
  validateResetToken: async (token) =>
    client.get("/reset-password/validate", { params: { token } }),

  changePassword: async ({ current_password, new_password, new_password_confirm }) =>
    client.post(
      "/change-password",
      { current_password, new_password, new_password_confirm },
      { headers: _authHeaders() },
    ),
};

// =====================================================================
// Social auth — Google (Emergent-managed) + Magic Link + provider linking
// =====================================================================
const socialClient = axios.create({
  baseURL: `${BASE}/api`,
  timeout: 20000,
});

export const socialApi = {
  /** POST /auth/social/callback — used by both Google callback and Magic
   *  Link consume paths. If `token` header is present, treats as link-mode. */
  callback: async ({ provider, session_id, token }, bearer = null) =>
    socialClient.post(
      "/auth/social/callback",
      { provider, session_id, token },
      bearer ? { headers: { Authorization: `Bearer ${bearer}` } } : undefined,
    ),

  requestMagicLink: async (email, remember_me = false) =>
    socialClient.post("/auth/social/magic-link/request", { email, remember_me }),

  consumeMagicLink: async (token) =>
    socialClient.post("/auth/social/magic-link/consume", { token }),

  /** Verify the 6-digit code sent alongside the magic-link. Returns
   *  the same {session_token, needs_profile_completion, ...} shape as
   *  /consume. */
  verifyMagicLinkCode: async (email, code) =>
    socialClient.post("/auth/social/magic-link/verify-code", { email, code }),

  listProviders: async () =>
    socialClient.get("/members/me/providers", { headers: _authHeaders() }),

  unlinkProvider: async (provider) =>
    socialClient.post(
      "/members/me/unlink-provider",
      { provider },
      { headers: _authHeaders() },
    ),

  completeProfile: async (callsign) =>
    socialClient.post(
      "/members/me/complete-profile",
      { callsign },
      { headers: _authHeaders() },
    ),
};

/** Emergent Google Auth redirect launcher.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH. Always derive redirect dynamically from window.location.
 */
export const startGoogleAuth = ({ next = "/members", link = false } = {}) => {
  // Use browser origin — never hardcode
  const returnPath = link ? "/auth/link/google" : "/auth/callback/google";
  const params = new URLSearchParams();
  if (next && next !== "/members") params.set("next", next);
  const queryString = params.toString();
  const redirectUrl =
    window.location.origin + returnPath + (queryString ? `?${queryString}` : "");
  window.location.href =
    `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};


// =====================================================================
// Operative Network — investigator contact graph + profile edit
// =====================================================================
const networkClient = axios.create({
  baseURL: `${BASE}/api`,
  timeout: 20000,
});

const _authHdr = () => {
  const t = getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export const operativeNetworkApi = {
  /** GET the full network snapshot — { accepted, pending_incoming,
   *  pending_outgoing, blocked_ids, counts }. */
  snapshot: async () =>
    networkClient.get("/operative-network", { headers: _authHdr() }),

  /** POST an Add-Operative request. Body: { callsign?, member_id?, note? } */
  request: async (body) =>
    networkClient.post("/operative-network/request", body,
      { headers: _authHdr() }),

  accept: async (contact_id) =>
    networkClient.post(`/operative-network/${contact_id}/accept`, {},
      { headers: _authHdr() }),

  decline: async (contact_id) =>
    networkClient.post(`/operative-network/${contact_id}/decline`, {},
      { headers: _authHdr() }),

  remove: async (contact_id) =>
    networkClient.post(`/operative-network/${contact_id}/remove`, {},
      { headers: _authHdr() }),

  block: async (contact_id, reason = null) =>
    networkClient.post(`/operative-network/${contact_id}/block`,
      { reason }, { headers: _authHdr() }),

  blockByCallsign: async (callsign) =>
    networkClient.post("/operative-network/block", { callsign },
      { headers: _authHdr() }),

  unblock: async (other_id) =>
    networkClient.post(`/operative-network/${other_id}/unblock`, {},
      { headers: _authHdr() }),

  /** PUT profile fields — interests, investigations, skills,
   *  display_name, region_generalized, discoverable */
  updateProfile: async (body) =>
    networkClient.put("/members/me/operative-profile", body,
      { headers: _authHdr() }),

  /** GET discovery search — reuses dispatch/member-search which now
   *  matches callsign / display_name / interests / investigations /
   *  skills, partial + case-insensitive, honours discoverable + blocks. */
  search: async (q, limit = 12) =>
    networkClient.get(`/dispatch/member-search?q=${encodeURIComponent(q)}&limit=${limit}`,
      { headers: _authHdr() }),
};
